import {
  BottomNavigation,
  Button,
  Chip,
  Controller,
  FileElement,
  FileManager,
  PageModal,
  TextEditor,
  Typography,
  useForm,
} from '@otp/ui';
import {
  IssueTicketStatus,
  IssueTicketsDocument,
  IssueTicketsQuery,
  UpdateWttpQuestionnaireQuestionResponseDto,
  WttpQuestion,
  WttpQuestionResponse,
  WttpQuestionResponseStatus,
} from 'src/graphql/generated/graphql';
import { WttpQuestionnaireResponseQuestionStatus } from '../../questionnaire-responses/question-status';
import { Attachment } from '@otp/icons';
import {
  mapFileAssetsToForm,
  mapFileAssetsToRequest,
} from 'src/components/common/resources/form/utils';
import { useTranslation } from 'react-i18next';
import { AttachmentsField } from 'src/components/common/resources/form/attachments-field';
import { Arrow } from '@otp/icons';
import { StatusField } from './fields/status';
import { useQuery } from '@apollo/client';
import { IssueTicketsPreviewList } from 'src/components/issue/tickets/preview-list/list';
import { IssueTicketForm } from 'src/components/issue/tickets/form';
import { useEffect, useMemo, useState } from 'react';

type Form = {
  status: WttpQuestionResponseStatus;
  comments: string;
  fileAssets: FileElement[];
};

type QuestionResponseFormProps = {
  storeId: string;
  question: Pick<
    WttpQuestion,
    'id' | 'title' | 'description' | 'category' | 'fileAssets'
  >;
  response?: Partial<WttpQuestionResponse>;
  onUpdateWttpQuestionResponse: (
    questionResponse: UpdateWttpQuestionnaireQuestionResponseDto,
  ) => void;
  onClose: () => void;
  onNavigateToNextQuestion: () => void;
  readonly?: boolean;
};

export const QuestionResponseForm = ({
  storeId,
  question,
  response,
  onUpdateWttpQuestionResponse,
  onClose,
  onNavigateToNextQuestion,
  readonly = false,
}: QuestionResponseFormProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'wttp.question-response',
  });

  const [addAnotherIssue, setAddAnotherIssue] = useState(false);

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    formState: { errors, isDirty, isValid },
  } = useForm<Form>({
    defaultValues: {
      status: response?.status || undefined,
      comments: response?.comments || undefined,
      fileAssets: mapFileAssetsToForm(response?.fileAssets || []),
    },
  });

  const { data: issueTicketsData, refetch: issueTicketsRefetch } =
    useQuery<IssueTicketsQuery>(IssueTicketsDocument, {
      variables: {
        filter: {
          storeId,
          wttpQuestionId: question.id,
          status: [IssueTicketStatus.Open, IssueTicketStatus.InProgress],
        },
      },
      fetchPolicy: 'cache-and-network',
    });

  const issueTickets = useMemo(
    () => issueTicketsData?.issueTickets?.records || [],
    [issueTicketsData],
  );
  const categoryName = question.category?.name;
  const questionFileElements = mapFileAssetsToForm(question.fileAssets || []);
  const responseStatus = watch('status');
  const isCommentsRequired =
    responseStatus === WttpQuestionResponseStatus.Fixed;

  useEffect(() => {
    const count = issueTickets.length;

    if (count && responseStatus !== WttpQuestionResponseStatus.Issue) {
      setValue('status', WttpQuestionResponseStatus.Issue, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else if (!count && responseStatus === WttpQuestionResponseStatus.Issue) {
      setValue('status', WttpQuestionResponseStatus.Okay, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [responseStatus, issueTickets, setValue]);

  const onStatusChange = (
    newValue: WttpQuestionResponseStatus,
    oldValue?: WttpQuestionResponseStatus,
  ) => {
    if (
      newValue === WttpQuestionResponseStatus.Issue &&
      oldValue !== WttpQuestionResponseStatus.Issue &&
      issueTickets.length === 0
    ) {
      setAddAnotherIssue(true);
    }
  };

  const onSubmit = async (form: Form) => {
    if (readonly) {
      return;
    }

    onUpdateWttpQuestionResponse({
      questionId: question.id,
      status: form.status || undefined,
      comments: form.comments,
      fileAssets: mapFileAssetsToRequest(form.fileAssets),
    });
  };

  const onBackToList = () => {
    if (isDirty) {
      handleSubmit(onSubmit)();
    }

    if (!isDirty || isValid) {
      onClose();
    }
  };

  const onNextQuestion = () => {
    if (isDirty) {
      handleSubmit(onSubmit)();
    }

    if (!isDirty || isValid) {
      onNavigateToNextQuestion();
    }
  };

  const onCancelIssueTicketForm = () => {
    setAddAnotherIssue(false);

    if (issueTickets.length === 0) {
      setValue('status', WttpQuestionResponseStatus.Okay);
    }
  };

  const onSaveIssueTicketForm = () => {
    setAddAnotherIssue(false);
    issueTicketsRefetch();
  };

  return (
    <PageModal open={true} onClose={onBackToList} title={question.title || ''}>
      <div className="w-full">
        <form>
          <div className="flex flex-row justify-between items-center gap-4">
            <div className="flex flex-row gap-2">
              <Chip label={categoryName} />
              {questionFileElements.length > 0 && <Attachment />}
            </div>
            <WttpQuestionnaireResponseQuestionStatus status={responseStatus} />
          </div>
          <div>
            <Typography className="my-4" variant="subtitle1">
              {question.title}
            </Typography>
            {question.description && (
              <Typography variant="subtitle2" className="mb-4">
                <div
                  dangerouslySetInnerHTML={{ __html: question.description }}
                ></div>
              </Typography>
            )}
          </div>
          <div className="mb-4">
            <Controller
              name="status"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div>
                  <Typography className="mb-2" variant="subtitle2">
                    {t('add-status').toUpperCase()}
                    <span className="text-red-500"> *</span>
                  </Typography>
                  <StatusField
                    field={field}
                    onChange={(value) => {
                      onStatusChange(
                        value.target.value as WttpQuestionResponseStatus,
                        field.value,
                      );
                      field.onChange(value);
                    }}
                    hasError={!!errors.status}
                    issuesCount={issueTickets.length}
                    disabled={readonly || issueTickets.length > 0}
                  />
                </div>
              )}
            />
          </div>
          <IssueTicketsPreviewList
            issueTickets={issueTickets}
            onAddAnotherIssue={() => setAddAnotherIssue(true)}
          />
          <div className="mb-4">
            <Controller
              name="comments"
              control={control}
              rules={{ required: isCommentsRequired }}
              render={({ field }) => (
                <div>
                  <Typography
                    className="mb-2"
                    variant="subtitle2"
                    color={isCommentsRequired && !!errors.comments ? 'red' : ''}
                  >
                    {t('note').toUpperCase()}
                    {isCommentsRequired && (
                      <span className="text-red-500"> *</span>
                    )}
                  </Typography>
                  {readonly && (
                    <Typography variant="subtitle2">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: field.value || t('no-note'),
                        }}
                      ></div>
                    </Typography>
                  )}
                  {!readonly && (
                    <TextEditor {...field} defaultValue={field.value} />
                  )}
                </div>
              )}
            />
          </div>
          <div className="mb-4">
            <Controller
              name="fileAssets"
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <div>
                  <Typography className="mb-2" variant="subtitle2">
                    {field.value.length > 0 &&
                      t('my-attachments').toUpperCase()}
                  </Typography>
                  <AttachmentsField
                    value={field.value}
                    onChange={(files) => {
                      return setValue(field.name, files, {
                        shouldDirty: true,
                      });
                    }}
                    reverseDirection={true}
                    buttonContent={t('my-attachments-button')}
                    buttonFullWidth={true}
                    fileDescription={false}
                    fileAccept={['video/*', 'image/*']}
                    readonly={readonly}
                  />
                </div>
              )}
            />
          </div>
          {questionFileElements.length > 0 && (
            <div>
              <Typography className="mb-2" variant="subtitle2">
                {t('troubleshooting-resources').toUpperCase()}
              </Typography>
              <FileManager readonly={true} files={questionFileElements} />
            </div>
          )}
          <BottomNavigation>
            <Button variant="outlined" className="grow" onClick={onBackToList}>
              <Arrow /> &nbsp; {t('back-to-list')}
            </Button>
            <Button
              variant="contained"
              className="grow"
              onClick={onNextQuestion}
            >
              {t('next-question')} &nbsp;{' '}
              <Arrow className="rotate-180" fill="white" />
            </Button>
          </BottomNavigation>
        </form>
      </div>
      {!readonly && addAnotherIssue && (
        <IssueTicketForm
          defaultStoreIds={[storeId]}
          defaultQuestionIds={[question.id]}
          disabledFields={{ storeIds: true }}
          onCancel={onCancelIssueTicketForm}
          onSave={onSaveIssueTicketForm}
        />
      )}
    </PageModal>
  );
};
